import {FC, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import classNames from 'classnames'
import {formatTransactions} from 'utils'
import {Button, ColumnSkeleton, RenderIf} from 'components/Basic'
import {
  CustomAddBypassFromNotificationsPayload,
  SSLErrorDetailsType,
  SSLErrorItemType,
  SSLType,
} from 'interfaces'
import {Accordion} from './Accordion'

interface SSLErrorDetailsProps {
  notificationDetails: SSLErrorDetailsType
  handleChange: (updatedItems: SSLErrorItemType[]) => void
  selectedSSLErrorViewType: SSLType
  setSelectedSSLErrorViewType: (type: SSLType) => void
  fetchData: () => void
  isNotificationsLoading: boolean
  isUpdateBypassLoading: boolean
  updateByPass: (
    bypassPayload: CustomAddBypassFromNotificationsPayload,
  ) => Promise<void>
  areMultipleItemsSelected: boolean
}
export const SSLErrorDetails: FC<SSLErrorDetailsProps> = ({
  notificationDetails,
  handleChange,
  selectedSSLErrorViewType,
  setSelectedSSLErrorViewType,
  fetchData,
  isNotificationsLoading,
  isUpdateBypassLoading,
  updateByPass,
  areMultipleItemsSelected,
}) => {
  const {t} = useTranslation(['common'])
  const {items, totalAppCount, totalUrlCount, offset} = notificationDetails
  const isByAppViewSelected = selectedSSLErrorViewType === SSLType.app
  const isAllItemExpanded = useMemo(
    () => items?.every(item => item.expand),
    [items],
  )

  const expandCollapseAll = (): void => {
    const itemsCopy = items.map(item => ({...item, expand: !isAllItemExpanded}))
    handleChange(itemsCopy)
  }

  return (
    <>
      <div
        id="ssl-error-tab-container"
        className="flex items-center justify-between border-b border-gray-800 px-6"
      >
        <div
          id="ssl-error-tab-collection"
          className="flex items-center cursor-pointer"
        >
          <Tab
            name={t('BY_APP')}
            count={totalAppCount}
            type={SSLType.app}
            isSelected={isByAppViewSelected}
            onChange={setSelectedSSLErrorViewType}
          />
          <Tab
            name={t('BY_URL')}
            count={totalUrlCount}
            type={SSLType.url}
            isSelected={!isByAppViewSelected}
            onChange={setSelectedSSLErrorViewType}
          />
        </div>
        <RenderIf isTrue={!isNotificationsLoading}>
          <Button
            data_cy="expand-collapse-button"
            bgColor="bg-transparent"
            textColor="gray-1100"
            fontSize="xs"
            className="karla"
            borderRadius="none"
            borderWidth="border-0"
            onClick={expandCollapseAll}
          >
            {isAllItemExpanded ? t('COLLAPSE_ALL') : t('EXPAND_ALL')}
          </Button>
        </RenderIf>
      </div>
      <RenderIf isTrue={isNotificationsLoading}>
        <div className="pl-6">
          <ColumnSkeleton width={200} height={8} classes="mt-4" />
          <ColumnSkeleton width={150} height={8} classes="mt-2" />
          <ColumnSkeleton width={100} height={8} classes="mt-2" />
        </div>
      </RenderIf>
      <RenderIf isTrue={!isNotificationsLoading}>
        <Accordion
          items={items}
          handleChange={handleChange}
          isByAppViewSelected={isByAppViewSelected}
          hasMore={
            isByAppViewSelected
              ? totalAppCount > offset
              : totalUrlCount > offset
          }
          fetchData={fetchData}
          updateByPass={updateByPass}
          areMultipleItemsSelected={areMultipleItemsSelected}
          isUpdateBypassLoading={isUpdateBypassLoading}
        />
      </RenderIf>
    </>
  )
}

interface TabProps {
  name: string
  count: number
  type: SSLType
  isSelected: boolean
  onChange: (type: SSLType) => void
}
const Tab: FC<TabProps> = ({name, count, type, isSelected, onChange}) => (
  <button
    className="h-6 flex items-center group mr-3"
    onClick={() => onChange(type)}
    data-cy={`notification-${type}-view-tab`}
  >
    <span
      className={classNames(
        'inline-block h-6 flex items-center karla-bold text-xs border-b-3 group-hover:border-white',
        {
          'border-transparent text-gray-1100': !isSelected,
          'border-white text-white': isSelected,
        },
      )}
    >
      {name}
    </span>
    <div
      data-cy="notification-count"
      className="flex items-center justify-center h-3.5 w-auto rounded bg-dope-purple-50 abc-bold text-8px text-dope-black ml-1 px-1"
    >
      {formatTransactions(count)}
    </div>
  </button>
)
